import React from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * CommentItem - Displays a single comment in chat-style format
 * @param {Object} comment - Comment object with id, comment, added_by_name, added_date, is_internal
 * @param {boolean} isCurrentUser - Whether this comment belongs to the current user
 */
const CommentItem = ({ comment, isCurrentUser = false }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  // Format timestamp to relative time (e.g., "2 minutes ago", "1 hour ago")
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return t('comments.justNow');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return t('comments.minutesAgo', { count: minutes });
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return t('comments.hoursAgo', { count: hours });
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return t('comments.daysAgo', { count: days });
    } else {
      // For older comments, show formatted date
      return date.toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  // Get author name from added_by_name field
  const authorName = comment.added_by_name || t('comments.anonymous');

  // Get author initials for avatar from added_by_name
  const getInitials = () => {
    if (comment.added_by_name) {
      const nameParts = comment.added_by_name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      return comment.added_by_name[0].toUpperCase();
    }
    return '?';
  };

  return (
    <div
      className={`flex gap-3 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-200 ${
        isCurrentUser ? 'flex-row-reverse' : ''
      }`}
      dir={isRTL ? (isCurrentUser ? 'ltr' : 'rtl') : undefined}
    >
      {/* Avatar */}
      <div className={`flex-shrink-0 ${isCurrentUser ? 'order-2' : ''}`}>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
          <span>{getInitials()}</span>
        </div>
      </div>

      {/* Comment Content */}
      <div
        className={`flex-1 min-w-0 ${
          isCurrentUser ? 'items-end' : 'items-start'
        } flex flex-col`}
      >
        {/* Author Name and Time */}
        <div
          className={`flex items-center gap-2 mb-1 ${
            isCurrentUser ? 'flex-row-reverse' : ''
          }`}
        >
          <span className="text-sm font-semibold text-gray-800">
            {authorName}
          </span>
          {comment.is_internal && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
              title={t('comments.internalComment')}
            >
              <Lock className="w-3 h-3" />
              {t('comments.internal')}
            </span>
          )}
          <span className="text-xs text-gray-500">
            {formatTimeAgo(comment.added_date)}
          </span>
        </div>

        {/* Comment Bubble */}
        <div
          className={`rounded-2xl px-4 py-3 shadow-sm max-w-[85%] ${
            isCurrentUser
              ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
              : 'bg-gray-800 text-white'
          }`}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-white">
            {comment.comment}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommentItem;

